import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { getAllowedTeams, getTeamOptions, applyTeamFilter } from '../../utils/teamPermissions';
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  PlusCircle,
  Trophy,
  Users,
  Edit2,
  Trash2,
  Target,
  AlertCircle
} from 'lucide-react';

const MatchesList = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTeam, setFilterTeam] = useState(() => {
    // Entrenador de Amateur empieza con "Todos", otros entrenadores con su equipo
    if (userProfile?.role === 'entrenador') {
      return userProfile.team === 'Amateur' ? 'Todos' : userProfile.team;
    }
    return 'Todos';
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [matchToDelete, setMatchToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const teams = useMemo(() => getTeamOptions(userProfile), [userProfile]);

  useEffect(() => {
    if (userProfile) {
      fetchMatches();
    }
  }, [filterTeam, userProfile]);

  const fetchMatches = async () => {
    try {
      setLoading(true);

      // Calcular allowedTeams DENTRO de la función
      const allowedTeams = getAllowedTeams(userProfile);

      let query = supabase
        .from('matches')
        .select('*')
        .order('date', { ascending: false })
        .order('start_time', { ascending: false });

      query = applyTeamFilter(query, allowedTeams, filterTeam);

      const { data, error } = await query;

      if (error) throw error;

      setMatches(data || []);
    } catch (error) {
      console.error('Error al cargar partidos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!matchToDelete) return;

    try {
      setDeleting(true);

      const { error } = await supabase
        .from('matches')
        .delete()
        .eq('id', matchToDelete.id);

      if (error) throw error;

      setMatches(matches.filter(m => m.id !== matchToDelete.id));
      setShowDeleteModal(false);
      setMatchToDelete(null);
    } catch (error) {
      console.error('Error al eliminar partido:', error);
      alert('Error al eliminar el partido');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.substring(0, 5);
  };

  const getTeamColor = (team) => {
    switch(team) {
      case 'Juvenil':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Cadete':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Amateur':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getResultColor = (result) => {
    switch(result) {
      case 'victoria':
        return 'bg-green-100 text-green-800';
      case 'empate':
        return 'bg-yellow-100 text-yellow-800';
      case 'derrota':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getResultLabel = (result) => {
    switch(result) {
      case 'victoria': return 'Victoria';
      case 'empate': return 'Empate';
      case 'derrota': return 'Derrota';
      default: return 'Pendiente';
    }
  };

  const isPast = (dateString, timeString) => {
    const matchDateTime = new Date(`${dateString}T${timeString}`);
    return matchDateTime < new Date();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando partidos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Partidos</h1>
                <p className="text-sm text-gray-600">{matches.length} partidos registrados</p>
              </div>
            </div>

            <button
              onClick={() => navigate('/matches/new')}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PlusCircle className="w-5 h-5" />
              Nuevo Partido
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filtros */}
        {userProfile?.role !== 'entrenador' && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700">Filtrar por equipo:</label>
              <select
                value={filterTeam}
                onChange={(e) => setFilterTeam(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {teams.map(team => (
                  <option key={team} value={team}>{team}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Lista de partidos */}
        {matches.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <Trophy className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay partidos</h3>
            <p className="text-gray-600 mb-6">Comienza registrando el primer partido</p>
            <button
              onClick={() => navigate('/matches/new')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PlusCircle className="w-5 h-5" />
              Crear Partido
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {matches.map((match) => {
              const past = isPast(match.date, match.start_time);

              return (
                <div
                  key={match.id}
                  className={`bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow ${
                    past ? 'opacity-90' : ''
                  }`}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getTeamColor(match.team)}`}>
                            {match.team}
                          </span>
                          {match.result && (
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getResultColor(match.result)}`}>
                              {getResultLabel(match.result)}
                            </span>
                          )}
                          {match.match_type && (
                            <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                              {match.match_type}
                            </span>
                          )}
                          {match.is_home ? (
                            <span className="text-xs text-gray-600">🏠 Local</span>
                          ) : (
                            <span className="text-xs text-gray-600">✈️ Visitante</span>
                          )}
                        </div>

                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          {match.team} vs {match.opponent}
                        </h3>

                        {(match.our_score !== null && match.opponent_score !== null) && (
                          <div className="text-3xl font-bold text-gray-900 mb-3">
                            {match.our_score} - {match.opponent_score}
                          </div>
                        )}

                        <div className="flex items-center gap-6 text-gray-600 mb-3">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span className="text-sm font-medium">{formatDate(match.date)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            <span className="text-sm">{formatTime(match.start_time)}</span>
                          </div>
                          {match.location && (
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              <span className="text-sm">{match.location}</span>
                            </div>
                          )}
                        </div>

                        {match.competition && (
                          <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                            <Trophy className="w-4 h-4" />
                            <span>{match.competition}</span>
                          </div>
                        )}

                        {match.formation && (
                          <div className="text-sm text-gray-600 mb-2">
                            <strong>Formación:</strong> {match.formation}
                          </div>
                        )}

                        {match.observations && (
                          <p className="text-sm text-gray-600 mt-3">{match.observations}</p>
                        )}
                      </div>

                      {/* Acciones */}
                      <div className="flex items-center gap-2 ml-6">
                        <button
                          onClick={() => navigate(`/matches/${match.id}/callup`)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Convocatoria"
                        >
                          <Users className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => navigate(`/matches/${match.id}/stats`)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Estadísticas"
                        >
                          <Target className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => navigate(`/matches/edit/${match.id}`)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {userProfile?.role === 'admin' && (
                          <button
                            onClick={() => {
                              setMatchToDelete(match);
                              setShowDeleteModal(true);
                            }}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Eliminar Partido</h3>
                <p className="text-sm text-gray-600">Esta acción no se puede deshacer</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700 mb-2">
                <strong>{matchToDelete?.team} vs {matchToDelete?.opponent}</strong>
              </p>
              <p className="text-sm text-gray-700">
                {formatDate(matchToDelete?.date)}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setMatchToDelete(null);
                }}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchesList;
